import { formatDeviceId, normalizeDeviceId, type DeviceOrganization, type OrganizationRef } from '@viewer/protocol';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api } from '../../lib/api/client';
import type { AppState } from '../../lib/session/controller';
import { StatusDot } from '../../ui/StatusDot';
import { formatDateTime } from '../org/labels';
import { AccessPasswordSettings } from './AccessPasswordSettings';
import styles from './HomeScreen.module.css';
import { EXAMPLE_DEVICES, MyDevices } from './MyDevices';
import { UnattendedConfirmDialog } from './UnattendedConfirmDialog';

interface Props {
  state: AppState;
  computerName: string;
  /** Falso no navegador: só acessa outros computadores. */
  canHost: boolean;
  onConnect(targetId: string): void;
  onSaveAccessPassword(password: string | null): Promise<void>;
  onLeaveOrganization(organizationId: string): void;
  onSetUnattended(organizationId: string, allowed: boolean): void;
  onOpenLogin(): void;
}

const connectionLabel: Record<AppState['connection'], string> = {
  online: 'Pronto para receber conexões',
  connecting: 'Conectando ao servidor...',
  offline: 'Sem conexão com o servidor',
};

export function HomeScreen({
  state,
  computerName,
  canHost,
  onConnect,
  onSaveAccessPassword,
  onLeaveOrganization,
  onSetUnattended,
  onOpenLogin,
}: Props) {
  const [targetInput, setTargetInput] = useState('');
  const [confirmingUnattended, setConfirmingUnattended] = useState<DeviceOrganization | null>(null);
  const [idCopied, setIdCopied] = useState(false);
  const copiedTimer = useRef<number | null>(null);
  const targetId = normalizeDeviceId(targetInput);
  const online = state.connection === 'online';
  const canConnect = online && /^\d{9}$/.test(targetId) && targetId !== state.deviceId;
  const managedBy = state.deviceOrganizations;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!canConnect) return;
    // Histórico pessoal só com conta; não atrasa o pedido de sessão.
    if (state.user) {
      void api.post('/api/me/access-history', { targetId }).catch(() => {});
    }
    onConnect(targetId);
  }

  useEffect(
    () => () => {
      if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
    },
    [],
  );

  async function copyId() {
    if (!state.deviceId) return;
    try {
      await navigator.clipboard.writeText(formatDeviceId(state.deviceId));
    } catch {
      return;
    }
    setIdCopied(true);
    if (copiedTimer.current !== null) window.clearTimeout(copiedTimer.current);
    copiedTimer.current = window.setTimeout(() => setIdCopied(false), 2000);
  }

  function leave(org: OrganizationRef) {
    const ok = confirm(
      `Remover ${org.name} deste computador?\n\nA empresa perde o acesso a ele e só volta se alguém do suporte vincular o computador de novo com um código.`,
    );
    if (ok) onLeaveOrganization(org.id);
  }

  // Liberar pede a palavra de confirmação; retirar precisa ser fácil.
  function toggleUnattended(org: DeviceOrganization, allowed: boolean) {
    if (allowed) return setConfirmingUnattended(org);
    const ok = confirm(
      `Retirar o acesso sem supervisão de ${org.name}?\n\nA partir de agora ela vai precisar da sua aprovação ou da senha de acesso. Se estiver conectada sem supervisão neste momento, a conexão é encerrada.`,
    );
    if (ok) onSetUnattended(org.id, false);
  }

  const statusText = online && !canHost ? 'Conectado ao servidor' : connectionLabel[state.connection];

  const serverOffline = state.connection === 'offline';

  return (
    <div className={styles.grid}>
      <div className={styles.mainColumn}>
        <section className={`${styles.card} ${styles.accessCard}`}>
          <h2 className={styles.cardTitle}>Acessar computador remoto</h2>
          <p className={styles.help}>Digite o ID do computador que você quer controlar.</p>
          <form className={styles.connectForm} onSubmit={submit}>
            <label className={styles.label} htmlFor="target-id">
              ID do computador remoto
            </label>
            <div className={styles.connectRow}>
              <input
                id="target-id"
                className={`${styles.input} ${styles.connectInput}`}
                inputMode="numeric"
                autoComplete="off"
                placeholder="000 000 000"
                value={targetInput}
                aria-describedby={serverOffline ? 'connect-offline' : undefined}
                onChange={(e) => setTargetInput(formatDeviceId(normalizeDeviceId(e.target.value).slice(0, 9)))}
              />
              <button type="submit" className={styles.primary} disabled={!canConnect} aria-disabled={!canConnect}>
                Conectar
              </button>
            </div>
            {serverOffline && (
              <p id="connect-offline" className={styles.connectHint}>
                Sem conexão com o servidor. Tente novamente em instantes.
              </p>
            )}
          </form>
        </section>

        <section className={`${styles.card} ${styles.computerCard}`}>
          <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>{canHost ? 'Meu computador' : 'Este navegador'}</h2>
            <p className={styles.statusBadge}>
              <StatusDot tone={online ? 'success' : state.connection === 'connecting' ? 'warning' : 'danger'} />
              {statusText}
            </p>
          </div>
          <p className={styles.computerName}>{computerName}</p>

          {canHost ? (
            <>
              <p className={styles.label}>Meu ID</p>
              <div className={styles.idRow}>
                <p className={styles.deviceId}>{state.deviceId ? formatDeviceId(state.deviceId) : '--- --- ---'}</p>
                <button type="button" className={styles.copyId} disabled={!state.deviceId} onClick={() => void copyId()}>
                  {idCopied ? 'Copiado' : 'Copiar'}
                </button>
              </div>
            </>
          ) : (
            <p className={styles.help}>
              Pelo navegador você acessa outros computadores. Para que este computador possa ser acessado, abra o
              aplicativo Viewer.
            </p>
          )}

          {canHost && <AccessPasswordSettings enabled={state.accessPassword} onSave={onSaveAccessPassword} />}

          {canHost && managedBy.length > 0 && (
            <>
              <p className={`${styles.label} ${styles.sectionLabel}`}>Empresas que gerenciam este computador</p>
              <ul className={styles.deviceList}>
                {managedBy.map((org) => (
                  <li key={org.id} className={styles.deviceRow}>
                    <span className={styles.deviceMain}>
                      <span className={styles.deviceName}>{org.name}</span>
                      <label className={styles.unattendedToggle}>
                        <input
                          type="checkbox"
                          checked={org.unattendedAllowed ?? false}
                          disabled={!online || org.unattendedAllowed === undefined}
                          onChange={(e) => toggleUnattended(org, e.target.checked)}
                        />
                        Permitir acesso sem supervisão
                      </label>
                      {online && org.unattendedAllowed === undefined && (
                        <span className={styles.deviceMeta}>
                          Indisponível: o servidor ainda não foi atualizado com esta opção.
                        </span>
                      )}
                      {org.lastUnattendedAt && (
                        <span className={styles.deviceMeta}>
                          Último acesso sem supervisão: {formatDateTime(org.lastUnattendedAt)}
                        </span>
                      )}
                    </span>
                    <button type="button" className={styles.smallSecondary} onClick={() => leave(org)}>
                      Remover
                    </button>
                  </li>
                ))}
              </ul>
              <p className={styles.help}>
                Colaboradores autorizados pedem acesso pela empresa: você aprova, eles usam a senha de acesso ou, se
                você permitir, entram sem supervisão. Outras pessoas só entram se você aceitar ou com a senha, e apenas
                se todas as empresas permitirem o acesso externo.
              </p>
            </>
          )}
        </section>
        {confirmingUnattended && (
          <UnattendedConfirmDialog
            organization={confirmingUnattended}
            onConfirm={() => {
              onSetUnattended(confirmingUnattended.id, true);
              setConfirmingUnattended(null);
            }}
            onCancel={() => setConfirmingUnattended(null)}
          />
        )}
      </div>

      <section className={`${styles.card} ${styles.devicesCard}`}>
        <h2 className={styles.cardTitle}>Seus dispositivos</h2>
        <div className={styles.devicesBody}>
          {state.user ? (
            <MyDevices organizations={state.organizations} canConnect={online} onConnect={onConnect} />
          ) : import.meta.env.DEV ? (
            <MyDevices organizations={[]} examples={EXAMPLE_DEVICES} canConnect={online} onConnect={onConnect} />
          ) : (
            <div className={styles.emptyDevices}>
              <p className={styles.help}>Veja os computadores das suas empresas e conecte com um clique.</p>
              <button type="button" className={styles.primary} onClick={onOpenLogin}>
                Entrar
              </button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
